import {ChangeDetectionStrategy, Component, computed, inject, signal} from '@angular/core';
import {KeyTestData, KeyTestDialogData, KeyTestFormField} from "./types/key-tests-modal.types";
import {MAT_DIALOG_DATA} from "@angular/material/dialog";
import {BaseModalComponent} from "../../../../../../core/components/modals/base-modal/base-modal.component";
import {MatExpansionModule,} from "@angular/material/expansion";
import {FormGroup, ReactiveFormsModule} from "@angular/forms";
import {keyTestFields} from "./configs/key-tests-helper";
import {keyTestDateFields, keyTestModalResourceType, keyTestTimeFields} from "./configs/key-tests-modal-config";
import {KeyTestsService} from "./services/key-tests.service";
import {defaultKeyTestData} from "./configs/defaul-key-test-data";
import {KeyTestFormComponent} from "./components/key-test-form/key-test-form.component";
import {KeyTestHeaderComponent} from "./components/key-test-header/key-test-header.component";
import {
  customDateTimeProvider
} from "../../../../../../core/locale/providers/custom-date-time.provider";
import {LoaderComponent} from "../../../../../../shared/components/loader/loader.component";
import {MatButton} from "@angular/material/button";

@Component({
  selector: 'app-key-tests-modal',
  imports: [
    BaseModalComponent,
    MatExpansionModule,
    ReactiveFormsModule,
    KeyTestFormComponent,
    KeyTestHeaderComponent,
    LoaderComponent,
    MatButton
  ],
  templateUrl: './key-tests-modal.component.html',
  styleUrl: './key-tests-modal.component.scss',
  providers: [ ...customDateTimeProvider ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class KeyTestsModalComponent extends BaseModalComponent {
  private keyTestDialogData = inject<KeyTestDialogData>(MAT_DIALOG_DATA);
  private keyTestsService: KeyTestsService = inject(KeyTestsService);

  public readonly keyTestModalResourceType = keyTestModalResourceType
  public keyTestFormGroups = signal<Array<FormGroup>>([]);
  public keyTestsData = signal<Array<KeyTestData>>([]);
  public isValid = signal<boolean>(false);
  public noteId = signal<number>(0);
  public parentData = computed(() => this.keyTestDialogData.parentData);
  public noFirstData = computed(() => this.keyTestsData().length > 1);

  public override async ngOnInit(): Promise<void> {
    super.ngOnInit();
    await this.initComponent();
  }

  public override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.modalStatesService.closeModal(keyTestModalResourceType.key);
  }

  public getTitleAccordion(index: number): string {
    const id = this.keyTestsData().at(index)?.id;
    return id ? `№${id}` : 'новый';
  }

  public handleAddNew() {
    const formGroup = this.baseModalService.getFormGroup<KeyTestFormField>(keyTestFields());
    this.keyTestFormGroups.update(forms  => [ ...forms, formGroup]);
    this.keyTestsData.update(data  => [ ...data, defaultKeyTestData ]);
    this.isValid.set(false);

  }

  public handleUpdateData(data: KeyTestData, index: number): void {
    this.keyTestsData()[index] = data;
    const keyTestData = this.convertData(data);
    this.keyTestFormGroups().at(index)?.patchValue(keyTestData);
    this.isValid.set(!!data.id);
  }

  public handleDeleteData(index: number): void {
    this.keyTestsData.update(data => {
      const newData = [...data];
      newData.splice(index, 1);
      return newData;
    });

    this.keyTestFormGroups.update(groups => {
      const newGroups = [...groups];
      newGroups.splice(index, 1);
      return newGroups;
    });

    this.isValid.set(true);
  }

  private async initComponent(): Promise<void> {
    this.modalStatesService.openModal(keyTestModalResourceType.key);
    this.loadingService.setIsLoadingState(true);
    this.noteId.set(Number(this.keyTestDialogData.parentData.id));
    await this.keyTestsService.fetchKeyTestsData(this.noteId())
      .then((data) => this.successFetch(data))
      .finally(() => this.loadingService.setIsLoadingState(false));
  }

  private successFetch(data: Array<KeyTestData>): void {
    this.keyTestsData.set(data);
    this.isValid.set(!!data.length);
    this.initForm();
  }

  private initForm(): void {
    if (!this.keyTestsData().length) {
      this.handleAddNew();
      return;
    }

    const formGroups = this.keyTestsData().map(data => {
      const formGroup = this.baseModalService.getFormGroup<KeyTestFormField>(keyTestFields());
      const keyTestData = this.convertData(data);

      formGroup.patchValue(keyTestData);
      return formGroup;
    });

    this.keyTestFormGroups.set(formGroups);
  }

  private convertData(data: KeyTestData): KeyTestData {
    const convertDate = this.baseModalService.convertDates<KeyTestData>(data, keyTestDateFields);
    return this.baseModalService.convertDateFieldsToRussianFormat<KeyTestData>(convertDate, keyTestTimeFields);
  }
}

